//import useUnload from "../components/UseUnload";
import { serverConfig } from "./serverConstants";
import { savePendingTestData, isSafeForSendBeacon } from "./indexedDBStorage";

const getBaseUrl = () => {
  return serverConfig.url;
};

// Global refs to store LATEST data - solves stale closure problem
let latestExamDetail = null;
let latestTestData = null;
let listenersInitialized = false;

/**
 * Sets up event handlers for saving test data when user leaves the page.
 * Uses:
 * 1. visibilitychange - Most reliable on mobile and modern browsers
 * 2. beforeunload - Fallback for desktop browsers
 * 3. IndexedDB - Async storage (no UI freeze, 500MB+ capacity)
 * 4. sendBeacon - Best effort network send for small payloads (<50KB)
 * 
 * IMPORTANT: This function updates global refs with latest data on every render,
 * but only adds event listeners ONCE to prevent duplicate saves.
 */
const onWindowReload = async (examId, examDetail, setWindowSwitch, setTestData, _windowSwitchCount, testData) => {
  
  // Update global refs with LATEST data on every render
  latestExamDetail = examDetail;
  latestTestData = testData;

  // Only add event listeners ONCE (on first call)
  if (listenersInitialized) {
    return; // Already set up, just updated the refs
  }
  listenersInitialized = true;

  // Shared save function - uses global refs for LATEST data
  const saveTestData = async (eventType) => {
    // Use global refs - always has latest data
    const currentExamDetail = latestExamDetail;
    const currentTestData = latestTestData;

    if (!currentExamDetail || !currentTestData) {
      console.log(`[${eventType}] No data to save`);
      return;
    }

    // Don't save if test is already submitted
    const currentStatus = currentExamDetail[0].status;
    if (currentStatus === "010" || currentStatus === "011" || currentStatus === "111" || currentStatus === "113" || currentStatus === "114") {
      console.log(`[${eventType}] Test already submitted, skipping save`);
      return;
    }

    // Don't save before the test has actually started - the Instructions screen (and the
    // gap between clicking "Start Exam" and /test/startExam resolving) sits at status "000".
    // Saving here forces status to "001" and upserts a fresh Mongo doc via /answeredQuestions
    // before /startExam ever ran, and that path never sets isTransferred - permanently
    // orphaning the doc from the postgres-transfer query.
    if (currentStatus === "000") {
      console.log(`[${eventType}] Test not started yet, skipping save`);
      return;
    }

    // Prepare data for saving
    let remainingTime = window.localStorage.getItem("remainingTime");
    const examDetailCopy = JSON.parse(JSON.stringify(currentExamDetail));

    examDetailCopy[0].status = "001";

    // Ensure time_remaining is always valid. Never fabricate a small number here - this
    // fires on tab close/refresh/backgrounding, so it's saved as this candidate's
    // "current" time and read back as real on their next resume. A `1` here is a
    // technically-valid number the backend's own fallback logic won't catch, and
    // produces a real false timeout for someone still actively taking the test.
    const isValidRemainingTime = remainingTime !== null && remainingTime !== undefined && remainingTime !== "" && !isNaN(Number(remainingTime));
    if (isValidRemainingTime) {
      examDetailCopy[0].time_remaining = Number(remainingTime);
    } else {
      const totalTestTime = examDetailCopy[0].total_test_time || (examDetailCopy[0].instanceduration * 60);
      examDetailCopy[0].time_remaining = totalTestTime;
      console.log(`[${eventType}] time_remaining null, falling back to full allotted duration:`, totalTestTime);
    }
    
    // Switches not yet taken off the allowance (saveToMongoFunction.js). Only the copy is changed;
    // the test's own examDetail keeps them until its next save.
    const pendingSwitches = Number(examDetailCopy[0].pendingWindowSwitches) || 0;
    examDetailCopy[0].webproctoringcount = Math.max(0, examDetailCopy[0].webproctoringcount - pendingSwitches);
    examDetailCopy[0].pendingWindowSwitches = 0;
    // Same as saveToMongoFunction.js: once the switch allowance is used up, keep the flag that
    // keeps window-switch checking on. Without it a refresh at 0 left the test unwatched - no
    // warning and no abort on the next switch.
    if (examDetailCopy[0].defaultZeroProctorCheck !== true && examDetailCopy[0].webproctoringcount === 0) {
      examDetailCopy[0].webproctoringzero = true;
    }

    const stringifyExamDetail = JSON.stringify(examDetailCopy);
    const encodedExamDetail = encodeURIComponent(btoa(encodeURIComponent(stringifyExamDetail)));

    const stringifyQuesData = JSON.stringify(currentTestData);
    const encodedQuesData = encodeURIComponent(btoa(encodeURIComponent(stringifyQuesData)));

    const baseUrl = getBaseUrl();
    const payload = JSON.stringify({
      examDetail: encodedExamDetail,
      id: currentExamDetail[0].id,
      quesData: encodedQuesData,
    });

    console.log(`[${eventType}] 📊 Saving ${currentTestData.length} questions with latest data`);

    // Step 1: ALWAYS save to IndexedDB first (async, non-blocking, large capacity)
    try {
      await savePendingTestData(currentExamDetail[0].id, {
        examDetail: encodedExamDetail,
        quesData: encodedQuesData,
      });
      console.log(`[${eventType}] 💾 Data saved to IndexedDB`);
    } catch (dbError) {
      console.error(`[${eventType}] IndexedDB save failed:`, dbError);
      // Fallback to localStorage if IndexedDB fails
      try {
        const pendingKey = `test-data-pending-${currentExamDetail[0].id}`;
        localStorage.setItem(pendingKey, JSON.stringify({
          userExamId: currentExamDetail[0].id,
          examDetail: encodedExamDetail,
          quesData: encodedQuesData,
          timestamp: new Date().toISOString(),
          retryCount: 0,
        }));
        console.log(`[${eventType}] 💾 Fallback: Data saved to localStorage`);
      } catch (lsError) {
        console.error(`[${eventType}] Both IndexedDB and localStorage failed!`);
      }
    }

    // Step 2: Try sendBeacon ONLY if payload is small enough (<50KB)
    if (isSafeForSendBeacon(payload)) {
      try {
        const blob = new Blob([payload], { type: "application/json" });
        // sendBeacon uses credentials mode 'include' per spec, so the HttpOnly
        // auth_token cookie is attached automatically (CORS allows credentials).
        const beaconSent = navigator.sendBeacon(
          `${baseUrl}/test/answeredQuestions`,
          blob
        );
        
        if (beaconSent) {
          console.log(`[${eventType}] 📤 Small payload sent via sendBeacon`);
        } else {
          console.warn(`[${eventType}] ⚠️ sendBeacon returned false`);
        }
      } catch (beaconError) {
        console.error(`[${eventType}] sendBeacon error:`, beaconError);
      }
    } else {
      console.log(`[${eventType}] 📦 Payload too large for sendBeacon (>50KB), will sync on resume`);
    }
  };

  // EVENT 1: visibilitychange - Most reliable on mobile and modern browsers
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'hidden') {
      console.log('[visibilitychange] Page hidden - saving test data...');
      await saveTestData('visibilitychange');
    }
  });

  // EVENT 2: beforeunload - Fallback for desktop browsers
  window.addEventListener('beforeunload', function (e) {
    // Don't save if no data
    if (!latestExamDetail) return;
    
    // Don't save if test is already submitted
    const currentStatus = latestExamDetail[0].status;
    if (currentStatus === "010" || currentStatus === "011" || currentStatus === "111" || currentStatus === "113" || currentStatus === "114") {
      return null;
    }

    // Trigger async save (best effort during unload)
    saveTestData('beforeunload');

    // Show browser's native confirmation dialog
    e.preventDefault();
    e.returnValue = "Your test progress will be saved. Are you sure you want to leave?";
    return e.returnValue;
  });

  // EVENT 3: pagehide - Additional fallback for Safari/iOS
  window.addEventListener('pagehide', async (e) => {
    if (!e.persisted) {
      console.log('[pagehide] Page being unloaded - saving test data...');
      await saveTestData('pagehide');
    }
  });
};

// Reset function for when user switches tests or logs out
export const resetReloadListeners = () => {
  latestExamDetail = null;
  latestTestData = null;
  // Note: We don't remove listeners as they check for null data
};

export default onWindowReload;